import { useCallback, useRef } from "react";
import {
    buildPaletteGradientCss,
    clampThresholdDrag,
} from "../../utils/luminancePalette";

export interface PaletteThresholdBarProps {
    colors: string[];
    thresholds: number[];
    onThresholdsChange: (next: number[]) => void;
}

/**
 * Horizontal luminance strip (0 -> 1) with piecewise color gradient and
 * draggable boundaries between palette colors.
 */
export function PaletteThresholdBar({
    colors,
    thresholds,
    onThresholdsChange,
}: PaletteThresholdBarProps) {
    const trackRef = useRef<HTMLDivElement | null>(null);

    const n = colors.length;
    const canEdit = n >= 2 && thresholds.length === n - 1;

    const setFromClientX = useCallback(
        (clientX: number, index: number) => {
            const el = trackRef.current;
            if (!el || !canEdit) return;
            const rect = el.getBoundingClientRect();
            const w = rect.width;
            if (w <= 0) return;
            const x = Math.max(0, Math.min(w, clientX - rect.left));
            const t = x / w;
            onThresholdsChange(clampThresholdDrag(thresholds, index, t));
        },
        [canEdit, onThresholdsChange, thresholds]
    );

    const onPointerDown = useCallback(
        (index: number) => (e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const move = (ev: PointerEvent) => {
                setFromClientX(ev.clientX, index);
            };
            const up = () => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
                window.removeEventListener("pointercancel", up);
            };

            window.addEventListener("pointermove", move);
            window.addEventListener("pointerup", up);
            window.addEventListener("pointercancel", up);

            setFromClientX(e.clientX, index);
        },
        [setFromClientX]
    );

    if (n <= 1) {
        return (
            <p className="upload-info path-tool-meta">
                Add at least two colors to edit luminance thresholds.
            </p>
        );
    }

    if (!canEdit) {
        return (
            <p className="upload-info path-tool-meta">
                Threshold count does not match palette (try reselecting a
                preset).
            </p>
        );
    }

    const bg = buildPaletteGradientCss(colors, thresholds);

    return (
        <div className="palette-threshold-bar">
            <div className="palette-threshold-bar__labels">
                <span>0</span>
                <span>Luminance</span>
                <span>1</span>
            </div>
            <div
                ref={trackRef}
                className="palette-threshold-bar__track"
                style={{ background: bg }}
            >
                {thresholds.map((t, index) => (
                    <button
                        key={index}
                        type="button"
                        className="palette-threshold-bar__handle"
                        style={{ left: `${t * 100}%` }}
                        onPointerDown={onPointerDown(index)}
                        aria-label={`Boundary between palette color ${
                            index + 1
                        } (left) and ${index + 2} (right) at luminance ${t.toFixed(
                            3
                        )}`}
                        aria-valuemin={0}
                        aria-valuemax={1}
                        aria-valuenow={t}
                    >
                        <span
                            className="palette-threshold-bar__swatch palette-threshold-bar__swatch--from"
                            style={{ backgroundColor: colors[index] }}
                            title={colors[index]}
                        />
                        <span
                            className="palette-threshold-bar__swatch palette-threshold-bar__swatch--to"
                            style={{ backgroundColor: colors[index + 1] }}
                            title={colors[index + 1]}
                        />
                    </button>
                ))}
            </div>
            <p className="palette-threshold-bar__hint upload-info path-tool-meta">
                Drag a handle to set where the next palette color begins (dark
                to light).
            </p>
        </div>
    );
}
